import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState, useMemo } from "react";
import { useLang } from "../contexts/LangContext";
import { enableStickyWatcher } from "../utils/stickyWatcher";
import { useUserData } from "../contexts/UserContext";
import { APP_CONFIG } from "../config/app.config";
import { authService } from "../services/auth.service";
import defaultAvatarImg from "../assets/images/default_avatar_dark.png";
import ModalWindow from "../components/ModalWindow";
import { resizeImageToBase64 } from "../utils/imageio";
import { convertNumberInCurrentLang, convertWeight } from "../utils/misc";
import { getNutrientData } from "../data/nutrientLoader";
import { fishDatabase } from "../data/fish_db";

export default function UserMode() {
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const { user, updateUser, getPerson } = useUserData();
  const currentUser = user;
  const [avatarImg, setAvatarImg] = useState<string>(
    user?.avatar || defaultAvatarImg
  );
  const [modalChildren, setModalChildren] = useState<React.ReactNode>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedMetric, setSelectedMetric] = useState<'totalWeight' | 'protein' | 'energy' | 'omega3'>('totalWeight');
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Get user's own person data
  const currentUserId = (currentUser as any).uuid ?? currentUser?.id;
  const userPerson = currentUserId ? getPerson(currentUserId) : null;

  // Calculate last 12 months stats
  const monthlyStats = useMemo(() => {
    if (!userPerson) return null;

    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();

    const months: Array<{
      month: number;
      year: number;
      totalWeight: number;
      protein: number;
      energy: number;
      omega3: number;
      isCurrent: boolean;
    }> = [];

    // Get last 12 months
    for (let i = 11; i >= 0; i--) {
      const targetDate = new Date(currentYear, currentMonth - i);
      const targetMonth = targetDate.getMonth();
      const targetYear = targetDate.getFullYear();

      // Use local date strings to avoid timezone issues
      const monthStart = new Date(targetYear, targetMonth, 1);
      const monthEnd = new Date(targetYear, targetMonth + 1, 0);
      const monthStartStr = `${targetYear}-${String(targetMonth + 1).padStart(2, '0')}-01`;
      const monthEndStr = `${targetYear}-${String(targetMonth + 1).padStart(2, '0')}-${String(monthEnd.getDate()).padStart(2, '0')}`;

      const monthRecords = userPerson.records.filter(record =>
        record.date >= monthStartStr && record.date <= monthEndStr
      );

      let totalWeight = 0;
      let protein = 0;
      let energy = 0;
      let omega3 = 0;

      monthRecords.forEach(record => {
        record.meals?.forEach(meal => {
          meal.fish_items?.forEach(item => {
            const weight = convertWeight(item.estimated_weight || 0, item.weight_unit || "gm", "gm");
            totalWeight += weight;

            const nutrients = getNutrientData(item.fish_id);
            if (nutrients) {
              const weightIn100g = weight / 100;
              protein += (nutrients.protein?.value || 0) * weightIn100g;
              energy += (nutrients.energy?.value || 0) * weightIn100g;
              omega3 += (nutrients.omega3?.value || 0) * weightIn100g;
            }
          });
        });
      });

      months.push({
        month: targetMonth,
        year: targetYear,
        totalWeight,
        protein,
        energy,
        omega3,
        isCurrent: targetMonth === currentMonth && targetYear === currentYear,
      });
    }

    return months;
  }, [userPerson]);

  // Calculate current month stats
  const currentMonthStats = useMemo(() => {
    if (!userPerson) return { 
      days: 0, 
      meals: 0, 
      weight: 0, 
      topFish: [], 
      topMeal: null, 
      topRecipe: null 
    };

    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const monthStartStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
    const monthEndStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(monthEnd.getDate()).padStart(2, '0')}`;

    const monthRecords = userPerson.records.filter(record =>
      record.date >= monthStartStr && record.date <= monthEndStr
    );

    let totalWeight = 0;
    let totalMeals = 0;
    const fishCount: Record<string, { count: number; weight: number; name_en: string; name_bn: string }> = {};
    const mealCount: Record<string, number> = {};
    const recipeCount: Record<string, number> = {};

    monthRecords.forEach(record => {
      const meals = record.meals?.filter(m => m.fish_items && m.fish_items.length > 0) || [];
      totalMeals += meals.length;

      meals.forEach(meal => {
        // Count meal types
        if (meal.meal_type) {
          mealCount[meal.meal_type] = (mealCount[meal.meal_type] || 0) + 1;
        }

        meal.fish_items?.forEach(item => {
          const weight = convertWeight(item.estimated_weight || 0, item.weight_unit || "gm", "gm");
          totalWeight += weight;

          // Count fish species
          if (!fishCount[item.fish_id]) {
            const fish = fishDatabase.find(f => f.id === item.fish_id);
            fishCount[item.fish_id] = {
              count: 0,
              weight: 0,
              name_en: fish?.english_name || "Unknown",
              name_bn: fish?.local_name || "অজানা",
            };
          }
          fishCount[item.fish_id].count++;
          fishCount[item.fish_id].weight += weight;

          // Count recipes
          if (item.recipe) {
            recipeCount[item.recipe] = (recipeCount[item.recipe] || 0) + 1;
          }
        });
      });
    });

    // Get top 3 fish by count
    const topFish = Object.entries(fishCount)
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, 3)
      .map(([id, data]) => ({ id, ...data }));

    // Get top meal
    const topMeal = Object.entries(mealCount)
      .sort((a, b) => b[1] - a[1])[0];

    // Get top recipe
    const topRecipe = Object.entries(recipeCount)
      .sort((a, b) => b[1] - a[1])[0];

    return {
      days: monthRecords.length,
      meals: totalMeals,
      weight: totalWeight,
      topFish,
      topMeal: topMeal ? { type: topMeal[0], count: topMeal[1] } : null,
      topRecipe: topRecipe ? { type: topRecipe[0], count: topRecipe[1] } : null,
    };
  }, [userPerson]);

  useEffect(() => {
    enableStickyWatcher();
  }, []);

  useEffect(() => {
    document.title = `User Mode - ${APP_CONFIG.NAME}`;  // Import APP_CONFIG if not already imported
  }, []);

  if (!currentUser) {
    return (
      <div className="p-8 text-center text-red-600">
        You must be signed in to view User Mode.
      </div>
    );
  }

  const getModalContent = () => {
    //console.log(avatarImg);
    return (
      <div className="bg-white bg-opacity-75 backdrop-blur-md rounded-3xl shadow-lg p-4 mx-scr">
        <div className="bg-white bg-opacity-50 rounded-xl flex flex-col items-center justify-center p-4">
          <img
            id="modalAvatarImg"
            src={avatarImg}
            alt="User Avatar"
            className="w-48 h-48 rounded-full mb-4"
          />
          <h2 className="text-2xl font-bold">{user?.name || "Default User"}</h2>
          <p className="text-gray-600">{user?.email || "No email provided"}</p>

          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            onChange={(e) => handleAvatarUpload(e)}
          />
          <button
            className="mt-4 button-xs bg-blue-500"
            onClick={() => fileInputRef.current?.click()}
          >
            {currentUser.avatar ? t("changeAvatar") : t("setAvatar")}
          </button>
        </div>
      </div>
    );
  };

  const getProfileOptionsModal = () => {
    // Format joined date
    const formatJoinedDate = (dateString?: string) => {
      if (!dateString) return null;
      const date = new Date(dateString);
      return date.toLocaleDateString(
        lang === "bn" ? "bn-BD" : "en-US",
        { year: 'numeric', month: 'long', day: 'numeric' }
      );
    };

    const joinedDate = userPerson?.added_on ? formatJoinedDate(userPerson.added_on) : null;
    const channelName = user?.channel_details?.name || user?.channel || null;

    return (
      <div className="bg-white rounded-3xl shadow-lg p-6 mx-scr max-w-md">
        {/* <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">{t("profileOptions")}</h2>
         */}
        <div className="flex flex-col items-center mb-6">
          <img
            src={avatarImg}
            alt="User Avatar"
            className="w-24 h-24 rounded-full mb-3"
          />
          <h3 className="text-xl font-semibold text-gray-800">{user?.name || "Default User"}</h3>
          <p className="text-gray-600 text-base">{user?.email || "No email provided"}</p>
          
          {/* Channel and Joined Date */}
          <div className="mt-3 w-full space-y-1">
            {channelName && (
              <div className="flex items-center justify-center gap-2 text-base text-gray-600">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418" />
                </svg>
                <span>{channelName}</span>
              </div>
            )}
            {joinedDate && (
              <div className="flex items-center justify-center gap-2 text-base text-gray-600">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
                </svg>
                <span>{lang === "bn" ? "যোগদান: " : "Joined: "}{joinedDate}</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col items-center gap-2">
          <button
            className="w-full button-xs grow bg-blue-500 text-white hover:bg-blue-600 transition-colors"
            onClick={() => {
              setModalChildren(null);
              navigate(`/profile/${currentUserId}`);
            }}
          >
            <div className="flex items-center justify-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="size-5" viewBox="0 0 24 24"><path fill="currentColor" d="M4 20v-2.8q0-.85.438-1.562T5.6 14.55q1.55-.775 3.15-1.162T12 13q.925 0 1.825.113t1.8.362l-1.675 1.7q-.5-.075-.975-.125T12 15q-1.4 0-2.775.338T6.5 16.35q-.225.125-.363.35T6 17.2v.8h6v2zm10 1v-3.075l5.525-5.5q.225-.225.5-.325t.55-.1q.3 0 .575.113t.5.337l.925.925q.2.225.313.5t.112.55t-.1.563t-.325.512l-5.5 5.5zm7.5-6.575l-.925-.925zm-6 5.075h.95l3.025-3.05l-.45-.475l-.475-.45l-3.05 3.025zm3.525-3.525l-.475-.45l.925.925zM12 12q-1.65 0-2.825-1.175T8 8t1.175-2.825T12 4t2.825 1.175T16 8t-1.175 2.825T12 12m0-2q.825 0 1.413-.587T14 8t-.587-1.412T12 6t-1.412.588T10 8t.588 1.413T12 10m0-2" /></svg>
              <span>{t("editProfile")}</span>
            </div>
          </button>

          <button
            className="w-full text-base underline text-red-700 hover:text-red-900 transition-colors"
            onClick={() => {
              setModalChildren(null);
              setShowDeleteConfirmation(true);
            }}
          >
            <div className="flex items-center justify-center gap-2">
              {/* <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
              </svg> */}
              <span>{t("deleteAccount")}{" "}</span>
            </div>
          </button>

          {/* <button
            className="w-full button-small bg-gray-500 text-gray-700 hover:bg-gray-300 transition-colors"
            onClick={() => setModalChildren(null)}
          >
            {t("cancel")}
          </button> */}
        </div>
      </div>
    );
  };

  const getDeleteConfirmationModal = () => {
    return (
      <div className="relative bg-white rounded-3xl shadow-lg mx-scr max-h-[75dvh] overflow-y-auto max-h-[80vh]">
        <div className="sticky top-0 rounded-t-3xl bg-white/50 backdrop-blur px-6 pt-6 pb-4">
          <h2 className="text-2xl font-bold text-red-600 text-center">{t("deleteAccountTitle")}</h2>
        </div>
        <div className="space-y-4 p-6">
          <div className="mb-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-center text-red-800 font-semibold mb-2">{t("deleteAccountWarning")}</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <p className="font-semibold text-gray-800 mb-2">{t("deleteAccountImplications")}</p>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                <li>{t("deleteAccountImplication1")}</li>
                <li>{t("deleteAccountImplication2")}</li>
                <li>{t("deleteAccountImplication3")}</li>
                <li>{t("deleteAccountImplication4")}</li>
              </ul>
            </div>

            <div className="mb-4">
              <label className="block text-center text-sm font-medium text-gray-700 mb-2">
                {t("deleteAccountConfirmPassword")}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder={t("deleteAccountPasswordPlaceholder")}
                  className="input-base input-small w-full pr-10"
                  disabled={deleteLoading}
                  // Bring input into view on focus (for mobile)
                  onFocus={(e) => e.target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })}
                />
                {deletePassword && (
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-gray-500">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-gray-500">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                      </svg>
                    )}
                  </button>
                )}
              </div>
            </div>

            {deleteError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-red-800 text-sm">{deleteError}</p>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <button
              className="w-full button-xs bg-red-600 text-white hover:bg-red-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              onClick={handleDeleteAccount}
              disabled={deleteLoading || !deletePassword}
            >
              {deleteLoading ? t("loading") : t("deleteAccountConfirmButton")}
            </button>

            <button
              className="w-full button-xs bg-gray-500 text-gray-700 hover:bg-gray-300 transition-colors"
              onClick={handleCancelDelete}
              disabled={deleteLoading}
            >
              {t("deleteAccountCancelButton")}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const setProfileAvatar = (avatar: string) => {
    setAvatarImg(avatar || defaultAvatarImg);
    //setModalChildren(getModalContent());
    updateUser({ ...currentUser, avatar: avatar || "" });
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const resized = await resizeImageToBase64(file);
      setProfileAvatar(resized);
      // Update modalAvatarImage
      const modalAvatarImg = document.getElementById(
        "modalAvatarImg"
      ) as HTMLImageElement;
      console.log(modalAvatarImg);
      if (modalAvatarImg) {
        modalAvatarImg.src = resized;
      }
    } catch (err) {
      window.alert("Image resize failed:" + err);
    }
  };

  const handleAvatarClick = () => {
    setModalChildren(getModalContent());
  };

  const handleProfileClick = () => {
    setModalChildren(getProfileOptionsModal());
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) return;

    setDeleteLoading(true);
    setDeleteError('');

    try {
      await authService.deleteAccount(deletePassword);
      
      // Show success message
      alert(t("deleteAccountSuccess"));
      
      // Navigate to login page
      navigate('/login');
    } catch (error: any) {
      console.error('Delete account error:', error);
      
      if (error.message === 'WRONG_PASSWORD') {
        setDeleteError(t("deleteAccountWrongPassword"));
      } else {
        setDeleteError(t("deleteAccountError"));
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setDeletePassword('');
    setDeleteError('');
    setShowPassword(false);
  };

  function goToUserLog(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
    event.stopPropagation();
    navigate(`/log-history/${currentUserId}`);
  }

  return (
    <div
      className={"relative flex flex-col justify-between grow bg-transparent"}
    >
      <div className="flex items-center justify-between gap-4 px-scr py-2 bg-slate-200 bg-opacity-80">
        <div className="flex items-center justify-start gap-3">
          <img
            onClick={() => handleAvatarClick()}
            src={avatarImg}
            className="w-10 h-10 rounded-full"
          />

          <h1 className="text-xl font-bold text-[#304050]">
            {user?.name || "Default User"}
          </h1>
        </div>
        <button
          className="button-xs bg-gray-500 !py-1 text-white"
          onClick={handleProfileClick}
        >
          <div>
            <span>{t("profile")}</span>
          </div>
        </button>
      </div>

      <div className="px-scr text-center mt-4 pb-16">
        <div>
          <h3 className="text-2xl font-bold mb-2 text-gray-700">Welcome</h3>
          <h1 className="text-4xl font-bold mb-8 text-pink-600">
            {user?.name}
          </h1>
          <p className="px-4 mb-6 text-base text-gray-700">{t("userModeDescription")}</p>
        </div>

        <div className="space-y-4">
          <button
            onClick={() => navigate("/user/new-log")}
            className="w-full button-base bg-blue-400 bg-opacity-25 backdrop-blur !rounded-3xl"
          >
            <div className="flex flex-col items-center justify-center text-blue-500 gap-2">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-white text-blue-500">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="size-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
              </div>
              {t("startNewDayLog")}
            </div>
          </button>

          <button
            onClick={goToUserLog}
            className="w-full button-base bg-pink-400 bg-opacity-25 backdrop-blur !rounded-3xl"
          >
            <div className="flex flex-col items-center justify-center text-pink-500 gap-2">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-white text-pink-500">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="size-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5m-9-6h.008v.008H12v-.008ZM12 15h.008v.008H12V15Zm0 2.25h.008v.008H12v-.008ZM9.75 15h.008v.008H9.75V15Zm0 2.25h.008v.008H9.75v-.008ZM7.5 15h.008v.008H7.5V15Zm0 2.25h.008v.008H7.5v-.008Zm6.75-4.5h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V15Zm0 2.25h.008v.008h-.008v-.008Zm2.25-4.5h.008v.008H16.5v-.008Zm0 2.25h.008v.008H16.5V15Z"
                  />
                </svg>
              </div>
              {t("viewHistory")}
            </div>
          </button>
        </div>

        {/* Dashboard Stats Section */}
        {userPerson && (
          <div className="mt-6 text-left">
            <div className="bg-purple-300 bg-opacity-20 backdrop-blur-sm rounded-3xl p-4">
              <h2 className="text-lg font-semibold text-[#304050] mb-3">
                {lang === "bn" ? "এই মাসের সংক্ষিপ্ত বিবরণ" : "This Month"}
              </h2>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-blue-100 bg-opacity-50 backdrop-blur p-3 rounded-xl">
                  <div className="text-sm text-gray-700">{lang === "bn" ? "দিন" : "Days"}</div>
                  <div className="text-2xl font-black text-blue-900">
                    {convertNumberInCurrentLang(lang, currentMonthStats.days)}
                  </div>
                </div>

                <div className="bg-blue-100 bg-opacity-50 backdrop-blur p-3 rounded-xl">
                  <div className="text-sm text-gray-700">{lang === "bn" ? "খাবার" : "Meals"}</div>
                  <div className="text-2xl font-black text-blue-900">
                    {convertNumberInCurrentLang(lang, currentMonthStats.meals)}
                  </div>
                </div>

                <div className="bg-blue-100 bg-opacity-50 backdrop-blur p-3 rounded-xl col-span-2">
                  <div className="text-sm text-gray-700">{lang === "bn" ? "মোট মাছ" : "Total Fish"}</div>
                  <div className="text-2xl font-black text-blue-900">
                    {convertNumberInCurrentLang(lang, Math.round(currentMonthStats.weight))} {lang === "bn" ? "গ্রাম" : "g"}
                  </div>
                </div>

                {/* Top Fish */}
                {currentMonthStats.topFish.length > 0 && (
                  <div className="bg-blue-100 bg-opacity-50 backdrop-blur p-3 rounded-xl col-span-2">
                    <div className="text-sm text-gray-700 mb-2">{lang === "bn" ? "শীর্ষ মাছ" : "Top Fish"}</div>
                    <div className="space-y-1">
                      {currentMonthStats.topFish.map((fish, idx) => (
                        <div key={fish.id} className="flex justify-between items-center text-sm">
                          <span className="text-blue-900 font-semibold">
                            {idx + 1}. {lang === "bn" ? fish.name_bn : fish.name_en}
                          </span>
                          <span className="text-blue-800">
                            {convertNumberInCurrentLang(lang, fish.count)} {lang === "bn" ? "বার" : "times"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Top Meal and Recipe */}
                <div className="bg-blue-100 bg-opacity-50 backdrop-blur p-3 rounded-xl">
                  <div className="text-sm text-gray-700">{lang === "bn" ? "শীর্ষ খাবার" : "Top Meal"}</div>
                  <div className="text-lg font-bold text-blue-900 mt-1">
                    {currentMonthStats.topMeal ? t(`mealType_${currentMonthStats.topMeal.type}`) : "-"}
                  </div>
                  {currentMonthStats.topMeal && (
                    <div className="text-xs text-blue-800">
                      {convertNumberInCurrentLang(lang, currentMonthStats.topMeal.count)} {lang === "bn" ? "বার" : "times"}
                    </div>
                  )}
                </div>

                <div className="bg-blue-100 bg-opacity-50 backdrop-blur p-3 rounded-xl">
                  <div className="text-sm text-gray-700">{lang === "bn" ? "শীর্ষ রেসিপি" : "Top Recipe"}</div>
                  <div className="text-lg font-bold text-blue-900 mt-1">
                    {currentMonthStats.topRecipe ? t(`whichRecipe_${currentMonthStats.topRecipe.type}`) : "-"}
                  </div>
                  {currentMonthStats.topRecipe && (
                    <div className="text-xs text-blue-800">
                      {convertNumberInCurrentLang(lang, currentMonthStats.topRecipe.count)} {lang === "bn" ? "বার" : "times"}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 12-Month Trends */}
            {monthlyStats && monthlyStats.length > 0 && (
              <div className="mt-4 bg-green-300 bg-opacity-20 backdrop-blur-sm rounded-3xl p-4">
                <h2 className="text-lg font-semibold text-[#304050] mb-3">
                  {lang === "bn" ? "১২ মাসের তুলনা" : "12-Month Trends"}
                </h2>

                {/* Metric Toggle Buttons */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <button
                    onClick={() => setSelectedMetric('totalWeight')}
                    className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      selectedMetric === 'totalWeight'
                        ? 'bg-teal-500 text-white shadow-md'
                        : 'bg-white bg-opacity-75 text-gray-700 hover:bg-opacity-70'
                    }`}
                  >
                    {lang === "bn" ? "মোট মাছ" : "Total Fish"}
                  </button>
                  <button
                    onClick={() => setSelectedMetric('protein')}
                    className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      selectedMetric === 'protein'
                        ? 'bg-emerald-500 text-white shadow-md'
                        : 'bg-white bg-opacity-75 text-gray-700 hover:bg-opacity-70'
                    }`}
                  >
                    {lang === "bn" ? "প্রোটিন" : "Protein"}
                  </button>
                  <button
                    onClick={() => setSelectedMetric('energy')}
                    className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      selectedMetric === 'energy'
                        ? 'bg-rose-500 text-white shadow-md'
                        : 'bg-white bg-opacity-75 text-gray-700 hover:bg-opacity-70'
                    }`}
                  >
                    {lang === "bn" ? "শক্তি" : "Energy"}
                  </button>
                  <button
                    onClick={() => setSelectedMetric('omega3')}
                    className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      selectedMetric === 'omega3'
                        ? 'bg-sky-500 text-white shadow-md'
                        : 'bg-white bg-opacity-75 text-gray-700 hover:bg-opacity-70'
                    }`}
                  >
                    {lang === "bn" ? "ওমেগা-৩" : "Omega-3"}
                  </button>
                </div>

                {/* Single Chart */}
                <ChartCard
                  title={
                    selectedMetric === 'totalWeight'
                      ? (lang === "bn" ? "মোট মাছ গ্রহণ" : "Total Fish Intake")
                      : selectedMetric === 'protein'
                      ? (lang === "bn" ? "প্রোটিন" : "Protein")
                      : selectedMetric === 'energy'
                      ? (lang === "bn" ? "শক্তি" : "Energy")
                      : (lang === "bn" ? "ওমেগা-৩" : "Omega-3")
                  }
                  data={monthlyStats}
                  dataKey={selectedMetric}
                  unit={
                    selectedMetric === 'totalWeight' || selectedMetric === 'protein'
                      ? (lang === "bn" ? "গ্রাম" : "g")
                      : selectedMetric === 'energy'
                      ? "kJ"
                      : "mg"
                  }
                  color={
                    selectedMetric === 'totalWeight'
                      ? 'bg-teal-500'
                      : selectedMetric === 'protein'
                      ? 'bg-emerald-500'
                      : selectedMetric === 'energy'
                      ? 'bg-rose-500'
                      : 'bg-sky-500'
                  }
                  lang={lang}
                />

                {/* Min/Max/Current Stats */}
                <div className="mt-3 flex justify-between items-center text-xs px-2">
                  <div className="text-gray-700">
                    <span className="font-semibold">{lang === "bn" ? "সর্বনিম্ন: " : "Min: "}</span>
                    <span>
                      {convertNumberInCurrentLang(
                        lang,
                        Math.round(Math.min(...monthlyStats.map(m => m[selectedMetric])))
                      )}
                      {" "}
                      {selectedMetric === 'totalWeight' || selectedMetric === 'protein'
                        ? (lang === "bn" ? "গ্রাম" : "g")
                        : selectedMetric === 'energy'
                        ? "kJ"
                        : "mg"}
                    </span>
                  </div>
                  <div className="text-blue-700 font-bold">
                    <span className="font-semibold">{lang === "bn" ? "বর্তমান: " : "Current: "}</span>
                    <span>
                      {convertNumberInCurrentLang(
                        lang,
                        Math.round(monthlyStats.find(m => m.isCurrent)?.[selectedMetric] || 0)
                      )}
                      {" "}
                      {selectedMetric === 'totalWeight' || selectedMetric === 'protein'
                        ? (lang === "bn" ? "গ্রাম" : "g")
                        : selectedMetric === 'energy'
                        ? "kJ"
                        : "mg"}
                    </span>
                  </div>
                  <div className="text-gray-700">
                    <span className="font-semibold">{lang === "bn" ? "সর্বোচ্চ: " : "Max: "}</span>
                    <span>
                      {convertNumberInCurrentLang(
                        lang,
                        Math.round(Math.max(...monthlyStats.map(m => m[selectedMetric])))
                      )}
                      {" "}
                      {selectedMetric === 'totalWeight' || selectedMetric === 'protein'
                        ? (lang === "bn" ? "গ্রাম" : "g")
                        : selectedMetric === 'energy'
                        ? "kJ"
                        : "mg"}
                    </span>
                  </div>
                </div>

                <div className="mt-4 bg-white bg-opacity-50 rounded-lg p-2">
                  <p className="text-xs text-gray-700">
                    {lang === "bn"
                      ? "চার্টটি গত ১২ মাসের মাসিক প্রবণতা দেখাচ্ছে। রঙিন বারটি বর্তমান মাস নির্দেশ করে।"
                      : "Chart shows monthly trends over the last 12 months. Colored bar indicates current month."}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bottom-nav-bar flex justify-center items-center w-full sticky bottom-0 px-6 py-4 bg-white bg-opacity-50 backdrop-blur z-10 border-t">
        <button
          className="button-xs bg-gray-500 text-gray-800"
          onClick={() => navigate("/survey")}
        >
          <span>{t("surveyMode")}</span>
        </button>
      </div>

      {modalChildren && (
        <ModalWindow
          isOpen={!!modalChildren}
          onClose={() => setModalChildren(null)}
        >
          {modalChildren}
        </ModalWindow>
      )}

      {showDeleteConfirmation && (
        <ModalWindow
          isOpen={showDeleteConfirmation}
          onClose={handleCancelDelete}
        >
          {getDeleteConfirmationModal()}
        </ModalWindow>
      )}
    </div>
  );
}

// Chart Card Component
function ChartCard({
  title,
  data,
  dataKey,
  unit,
  color,
  lang,
}: {
  title: string;
  data: Array<{ month: number; year: number; [key: string]: any; isCurrent: boolean }>;
  dataKey: string;
  unit: string;
  color: string;
  lang: string;
}) {
  const maxValue = Math.max(...data.map(d => d[dataKey]), 1);

  return (
    <div className="bg-white bg-opacity-75 rounded-2xl shadow-md p-4 backdrop-blur">
      <h4 className="text-sm font-semibold text-gray-700 mb-2">{title}</h4>
      
      {/* Chart */}
      <div className="relative px-1">
        <div className="flex items-end gap-0.5 h-16 relative">
          {data.map((monthData, idx) => {
            const heightPercent = maxValue > 0 ? (monthData[dataKey] / maxValue) * 100 : 0;
            return (
              <div key={idx} className="flex-1 relative h-full">
                {/* Vertical grid line */}
                <div className="absolute bottom-0 left-0 w-px h-full bg-gray-200" style={{ opacity: 0.3 }} />
                
                {/* Bar */}
                <div className="absolute bottom-0 left-0 right-0 flex items-end">
                  <div
                    className={`w-full rounded-sm transition-all duration-200 ${
                      monthData.isCurrent ? color : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                    style={{ height: `${heightPercent * 0.64}px`, minHeight: monthData[dataKey] > 0 ? '2px' : '0' }}
                    title={`${convertNumberInCurrentLang(lang, parseFloat(monthData[dataKey].toFixed(1)))} ${unit}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Month labels */}
        <div className="flex gap-0.5 mt-0.5">
          {data.map((monthData, idx) => {
            const date = new Date(monthData.year, monthData.month);
            const monthLabel = date.toLocaleDateString(
              lang === "bn" ? "bn-BD" : "en-US",
              { month: "short" }
            );
            const yearLabel = date.toLocaleDateString(
              lang === "bn" ? "bn-BD" : "en-US",
              { year: "2-digit" }
            );
            return (
              <div
                key={idx}
                className={`flex-1 text-center ${monthData.isCurrent ? 'font-semibold text-blue-600' : 'text-gray-500'}`}
                style={{ fontSize: '0.5rem', lineHeight: '0.65rem' }}
              >
                <div>{monthLabel}</div>
                <div>{yearLabel}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
